import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { ArrowRight, LogOut, Link as LinkIcon, ExternalLink, QrCode } from "lucide-react";
import { Layout } from "@/components/Layout";
import { LanguageToggle } from "@/components/LanguageToggle";
import { getToken, clearToken, authHeader } from "@/lib/auth";
import DepotsPanel from "@/components/praticien/DepotsPanel";
import DriversSeancesPanel from "@/components/praticien/DriversSeancesPanel";
import TdahParentsPanel from "@/components/praticien/TdahParentsPanel";
import ActivationsPanel from "@/components/praticien/ActivationsPanel";
import { ModuleCatalog } from "@/components/ModuleCatalog";
import { PRAT_T, PATIENT_MODULES } from "@/data/praticien";
import { MODULE_CATEGORIES } from "@/data/moduleCatalog";
import { DashSection, NewBadge, openSection } from "@/components/praticien/DashSection";
import { DashSearch, filterCatalog } from "@/components/praticien/DashSearch";

const byKey = Object.fromEntries(PATIENT_MODULES.map((m) => [m.key, m]));
const moduleGroups = [
  ...MODULE_CATEGORIES.map((c) => ({ title: c.title, modules: c.modules.map((m) => byKey[m.key]).filter(Boolean) })),
  { title: "Autres", modules: PATIENT_MODULES.filter((m) => !MODULE_CATEGORIES.some((c) => c.modules.some((x) => x.key === m.key))) },
].filter((g) => g.modules.length);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
  const [lang, setLang] = useState("fr");
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [patientName, setPatientName] = useState("");
  const [linkLang, setLinkLang] = useState("fr");
  const [qrOpen, setQrOpen] = useState(null);
  const [selectedModule, setSelectedModule] = useState("");
  const [query, setQuery] = useState("");
  const [badges, setBadges] = useState({ depots: 0, drivers: 0 });
  const setBadge = (k) => (n) => setBadges((b) => (b[k] === n ? b : { ...b, [k]: n }));
  const navigate = useNavigate();
  const t = PRAT_T[lang];
  const filteredCatalog = filterCatalog(query);

  useEffect(() => {
    if (!getToken()) { navigate("/praticien"); return; }
    axios.get(`${API}/auth/me`, { headers: authHeader() })
      .then((r) => { setUser(r.data); setChecking(false); })
      .catch(() => { clearToken(); navigate("/praticien"); });
  }, [navigate]);

  const logout = () => { clearToken(); navigate("/praticien"); };

  const buildLink = (key) => {
    const q = new URLSearchParams();
    const name = patientName.trim();
    if (name) q.set("nom", name);
    if (linkLang === "es") q.set("lang", "es");
    const qs = q.toString();
    return `${window.location.origin}/p/${key}${qs ? `?${qs}` : ""}`;
  };

  const copyPatientLink = async (key) => {
    try {
      await navigator.clipboard.writeText(buildLink(key));
      toast.success(t.linkCopied, { description: buildLink(key) });
    } catch {
      toast.error(t.copyError);
    }
  };

  if (checking) {
    return <Layout wide><p className="text-center text-[var(--ink-soft)] py-20" data-testid="prat-loading">{t.loading}</p></Layout>;
  }

  return (
    <Layout wide>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6 sm:mb-8 no-print">
        <span className="text-sm text-[var(--ink-soft)] break-all" data-testid="prat-user-email">{user?.email}</span>
        <div className="flex items-center gap-3 flex-wrap">
          <LanguageToggle lang={lang} onChange={setLang} />
          <button onClick={logout} data-testid="prat-logout-btn" className="inline-flex items-center gap-1.5 text-sm px-4 py-1.5 rounded-full border border-[var(--gold)] text-[var(--gold)] hover:bg-[var(--gold)] hover:text-white transition-colors">
            <LogOut size={15} /> {t.logout}
          </button>
        </div>
      </div>

      <div className="text-center mb-10 animate-fade-up">
        <h1 className="font-serif-title text-4xl sm:text-5xl text-[var(--gold)] mb-3">{t.dashboardTitle}</h1>
        <p className="text-[var(--ink-soft)] max-w-xl mx-auto leading-relaxed">{t.dashboardIntro}</p>
      </div>

      <DashSearch query={query} onQuery={setQuery} copyPatientLink={copyPatientLink} />

      <Link to="/praticien/patients" data-testid="prat-fiches-link"
        className="group flex flex-col sm:flex-row sm:items-center justify-between gap-4 rounded-3xl bg-[var(--gold)] text-white p-6 sm:p-7 mb-10 shadow-sm hover:brightness-105 transition-all">
        <div className="pr-2">
          <h2 className="font-serif-title text-2xl sm:text-3xl mb-1">{t.ficheTitle}</h2>
          <p className="text-sm text-white/85 leading-relaxed max-w-2xl">{t.ficheDesc}</p>
        </div>
        <span className="inline-flex items-center gap-1.5 text-sm bg-white/20 px-5 py-2.5 rounded-full whitespace-nowrap shrink-0">{t.ficheOpen} <ArrowRight size={16} /></span>
      </Link>

      {query.trim() && filteredCatalog.length === 0 && <p className="text-sm text-[var(--ink-soft)] italic text-center" data-testid="prat-catalog-empty">Aucun module ne correspond à « {query.trim()} ».</p>}
      <ModuleCatalog
        categories={filteredCatalog}
        cardTestid="prat-tool"
        renderActions={(m) => (
          <div className="pt-4 border-t border-[var(--border)] flex items-center justify-end">
            {m.prescription ? (
              <button type="button" onClick={() => openSection("tdah-parents")} data-testid={`prat-tool-open-${m.key}`} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[var(--gold)] text-white text-sm font-medium hover:bg-[var(--gold-light)] transition-all shadow-sm active:scale-95">Gérer les dossiers parents <ArrowRight size={16} /></button>
            ) : (
            <Link
              to={m.path}
              data-testid={`prat-tool-open-${m.key}`}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[var(--gold)] text-white text-sm font-medium hover:bg-[var(--gold-light)] transition-all shadow-sm active:scale-95"
            >
              {t.openTool} <ArrowRight size={16} />
            </Link>
            )}
          </div>
        )}
      />

      <div className="mt-14 pt-10 border-t border-[var(--border)]">
        <h2 className="font-serif-title text-3xl text-[var(--gold)] mb-2">{lang === "es" ? "Seguimiento y enlaces" : "Suivi et liens"}</h2>
        <p className="text-sm text-[var(--ink-soft)] max-w-2xl leading-relaxed">{lang === "es" ? "Cada bloque se abre con un clic." : "Chaque bloc s'ouvre d'un clic."}</p>
      </div>

      <DashSection id="liens-patients" testid="prat-patient-links" title={t.patientLinksTitle} subtitle={t.patientLinksIntro}>
        <div className="grid sm:grid-cols-2 gap-4 mb-5">
          <div>
            <label htmlFor="patient-name" className="block text-sm text-[var(--ink)] mb-2 font-medium">{t.patientNameLabel}</label>
            <input id="patient-name" data-testid="patient-name-input" value={patientName} onChange={(e) => setPatientName(e.target.value)} placeholder={t.patientNamePlaceholder}
              className="w-full rounded-xl border border-[var(--border)] bg-[var(--sand)] px-4 py-2.5 text-[var(--ink)] focus:outline-none focus:border-[var(--gold)]" />
            <div className="flex items-center gap-3 mt-3" data-testid="patient-link-lang">
              <span className="text-sm text-[var(--ink)] font-medium">{t.linkLangLabel}</span>
              <LanguageToggle lang={linkLang} onChange={setLinkLang} />
            </div>
          </div>
          <div>
            <label htmlFor="patient-module" className="block text-sm text-[var(--ink)] mb-2 font-medium">{lang === "es" ? "Módulo" : "Module"}</label>
            <select id="patient-module" value={selectedModule} onChange={(e) => setSelectedModule(e.target.value)} data-testid="patient-module-select"
              className="w-full rounded-xl border border-[var(--border)] bg-white px-4 py-2.5 text-[var(--ink)] focus:outline-none focus:border-[var(--gold)]">
              <option value="">{lang === "es" ? "— Elegir un módulo —" : "— Choisir un module —"}</option>
              {moduleGroups.map((g) => <optgroup key={g.title} label={g.title}>{g.modules.map((m) => <option key={m.key} value={m.key}>{m[lang]}</option>)}</optgroup>)}
              <option value="all">{lang === "es" ? "Mostrar todos los módulos" : "Afficher tous les modules"}</option>
            </select>
          </div>
        </div>

        {!selectedModule && <p className="text-sm text-[var(--ink-soft)] italic" data-testid="patient-link-placeholder">{lang === "es" ? "Elija un módulo para obtener su enlace, el código QR y la apertura en una pestaña nueva." : "Choisissez un module pour obtenir son lien, son QR code et l'ouverture dans un nouvel onglet."}</p>}
        <div className={selectedModule === "all" ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" : "max-w-xl"}>
          {PATIENT_MODULES.filter((m) => selectedModule === "all" || m.key === selectedModule).map((m) => {
            const url = buildLink(m.key);
            const isQrOpen = qrOpen === m.key;
            if (m.key === "super-cerveau") return (
              <div key={m.key} data-testid={`patient-link-row-${m.key}`} className="flex flex-col gap-3 rounded-2xl bg-white/60 border border-[var(--border)] p-4">
                <span className="font-medium text-[var(--ink)] leading-snug">{m[lang]}</span>
                <p className="text-xs text-[var(--ink-soft)]">Module sur prescription : il s'ouvre uniquement avec un lien d'activation unique par famille.</p>
                <button type="button" onClick={() => openSection("activations")} data-testid="patient-link-activation-super-cerveau" className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-full bg-[var(--gold)] text-white hover:brightness-105 transition-all w-fit"><LinkIcon size={14} /> Créer un lien d'activation</button>
              </div>
            );
            return (
              <div key={m.key} data-testid={`patient-link-row-${m.key}`} className="flex flex-col gap-3 rounded-2xl bg-white/60 border border-[var(--border)] p-4">
                <span className="font-medium text-[var(--ink)] leading-snug">{m[lang]}</span>
                <div className="rounded-xl bg-[var(--sand)] border border-[var(--border)] px-3 py-2 overflow-x-auto">
                  <code data-testid={`patient-link-url-${m.key}`} className="text-xs text-[var(--ink-soft)] break-all">{url}</code>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <button onClick={() => copyPatientLink(m.key)} data-testid={`patient-link-copy-${m.key}`} className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-full bg-[var(--gold)] text-white hover:brightness-105 transition-all"><LinkIcon size={14} /> {t.copyPatientLink}</button>
                  <a href={url} target="_blank" rel="noopener noreferrer" data-testid={`patient-link-open-${m.key}`} className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-full border border-[var(--gold)] text-[var(--gold)] hover:bg-[var(--gold)] hover:text-white transition-colors"><ExternalLink size={14} /> {t.openNewTab}</a>
                  <button onClick={() => setQrOpen(isQrOpen ? null : m.key)} data-testid={`patient-link-qr-${m.key}`} className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-full border border-[var(--border)] text-[var(--ink-soft)] hover:border-[var(--gold)] hover:text-[var(--gold)] transition-colors"><QrCode size={14} /> {isQrOpen ? t.hideQr : t.showQr}</button>
                </div>
                {isQrOpen && (
                  <div className="flex flex-col items-center gap-2 pt-2 border-t border-[var(--border)]" data-testid={`patient-link-qr-box-${m.key}`}>
                    <div className="rounded-xl bg-white p-3 border border-[var(--border)]"><QRCodeSVG value={url} size={148} bgColor="#ffffff" fgColor="#4A3B36" level="M" /></div>
                    <span className="text-xs text-[var(--ink-soft)] text-center">{t.qrHint}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </DashSection>

      <DashSection id="activations" testid="prat-activations" eyebrow="Enfant & famille — TDAH" title="Liens d'activation — Mission « Super-Cerveau »"
        subtitle="Module sur prescription : chaque famille reçoit un lien unique qui active le jeu sur son appareil. Le lien reste valable tant que vous ne le révoquez pas ; non utilisé, il expire après 30 jours. Rattachez chaque lien à un dossier patient pour retrouver la famille en un clic.">
        <ActivationsPanel compact />
      </DashSection>

      <DashSection id="depots" testid="prat-depots" title={lang === "es" ? "Depósito de cuaderno (paciente → profesional)" : "Dépôt de carnet (patient → praticien)"} badge={<NewBadge n={badges.depots} testid="depot-new-badge" />}
        subtitle={lang === "es" ? "Genere un enlace de depósito seguro y envíelo a su paciente. Podrá depositar su cuaderno en PDF; lo encontrará aquí para descargarlo en sesión." : "Générez un lien de dépôt sécurisé et transmettez-le à votre patient. Il pourra y déposer son carnet PDF ; vous le retrouverez ici pour le télécharger en séance."}>
        <DepotsPanel lang={lang} onBadge={setBadge("depots")} />
      </DashSection>

      <DashSection id="drivers-seances" testid="drv-seances-panel" eyebrow="Drivers relationnels — Inventaire & travail" title="Séances patients" badge={<NewBadge n={badges.drivers} testid="drv-new-badge" />}
        subtitle="Inventaire en 3 temps, cahier « Travail autour des drivers », regards des proches et classement du praticien — les résultats arrivent ici, sans dépôt.">
        <DriversSeancesPanel lang={lang} onBadge={setBadge("drivers")} />
      </DashSection>

      <DashSection id="tdah-parents" testid="tp-panel" eyebrow="Enfant & famille — TDAH" title="Évaluation parentale — perception croisée"
        subtitle="Créez un dossier enfant : deux liens distincts sont générés (Parent 1 / Parent 2). Chaque parent répond seul sur son téléphone ; aucun parent ne voit les réponses de l'autre. Les réponses arrivent ici pour comparaison, lecture clinique et export PDF.">
        <TdahParentsPanel />
      </DashSection>

      <DashSection id="entourage" testid="prat-entourage" title={lang === "es" ? "Drivers — la mirada de un allegado" : "Drivers — le regard d'un proche"}>
        <p className="text-sm text-[var(--ink-soft)] max-w-2xl leading-relaxed mb-5">
          {lang === "es"
            ? "Al final del cuaderno « Drivers relacionales », el paciente puede invitar a un allegado mediante un enlace: este describe cómo percibe sus drivers y propone su propia clasificación. Solo el paciente ve la comparación. Comparta con el paciente el enlace del módulo Drivers; encontrará el botón « Invitar a un allegado » al final del ejercicio."
            : "À la fin du cahier « Drivers relationnels », le patient peut inviter un proche via un lien : celui-ci décrit comment il perçoit ses drivers et propose son propre classement. Seul le patient voit la comparaison. Transmettez-lui le lien du module Drivers ; il trouvera le bouton « Inviter un proche » à la fin de l'exercice."}
        </p>
        <button onClick={() => copyPatientLink("drivers-exo")} data-testid="prat-entourage-copy-link" className="inline-flex items-center gap-1.5 text-sm px-4 py-2.5 rounded-full bg-[var(--gold)] text-white hover:brightness-105 transition-all">
          <LinkIcon size={15} /> {lang === "es" ? "Copiar el enlace del módulo Drivers" : "Copier le lien du module Drivers"}
        </button>
      </DashSection>

      <DashSection id="infos-pratiques" testid="prat-infos-pratiques" title={t.infosTitle} subtitle={t.infosDesc}>
        <div className="flex flex-wrap items-center gap-3">
          <a href="/infos-pratiques" target="_blank" rel="noopener noreferrer" data-testid="infos-open-link" className="inline-flex items-center gap-1.5 text-sm px-4 py-2.5 rounded-full bg-[var(--gold)] text-white hover:brightness-105 transition-all"><ExternalLink size={15} /> {t.infosOpen}</a>
          <button onClick={async () => { const link = `${window.location.origin}/infos-pratiques`; try { await navigator.clipboard.writeText(link); toast.success(t.linkCopied, { description: link }); } catch { toast.error(t.copyError); } }}
            data-testid="infos-copy-link" className="inline-flex items-center gap-1.5 text-sm px-4 py-2.5 rounded-full border border-[var(--gold)] text-[var(--gold)] hover:bg-[var(--gold)] hover:text-white transition-colors"><LinkIcon size={15} /> {t.infosCopy}</button>
        </div>
      </DashSection>
    </Layout>
  );
}
